/**
 * Hide internal stack/vendor names from client UI (English only).
 * Never show: Duplikium, Supabase, Cloudflare, Plisio, cTrader, MT4, MT5, etc.
 */

const DEFAULT_FALLBACK =
  "Something went wrong. Please try again. If this continues, contact support.";

/** Vendor / infra names the client must never see in error text. */
const VENDOR_RE =
  /\bduplikium\b|\bsupabase\b|\bcloudflare\b|\bplisio\b|\bctrader\b|\bmt4\b|\bmt5\b|\bpostgres(?:ql|t)?\b|\bresend\b|\bnowpayments\b|\bccpayment\b/i;

const INTERNAL_RE = new RegExp(
  [
    VENDOR_RE.source,
    "schema\\s*cache",
    "client_duplikium",
    "_slaves",
    "paid_slots",
    "recaptcha",
    "\\.env\\b",
    "npm\\s+run",
    "\\bnpm\\b",
    "trade[- ]?copier",
    "openapi",
    "localhost",
    "127\\.0\\.0\\.1",
    "\\bapi\\b",
    "embedded\\s+api",
    "non-json",
    "\\.json\\b",
    "\\bsql\\b",
    "pgcrypto",
    "relation\\s*[`\"]?public",
    "could not find the (table|column)",
    "HTTP\\s+\\d{3}\\b",
    "credentials in \\.env",
    "broker-servers",
    "server configuration error",
    "lookup failed",
    "insert failed",
    "read failed",
    "update failed",
    "supabase insert",
    "scheduleSlaveConnectSetup",
    "\\bis not defined\\b",
    "\\bslave\\b",
    "\\bmaster\\b",
  ].join("|"),
  "i",
);

function mapKnown(raw) {
  const s = String(raw || "").trim();
  const low = s.toLowerCase();

  if (/saisis|impossible de|veuillez|identifiants incorrects|«|»|échec|erreur serveur/i.test(s)) {
    return DEFAULT_FALLBACK;
  }
  if (/subscription_path|schema cache|could not find the .*column/i.test(s)) {
    return "We could not save your account settings. Please try again in a few minutes or contact support.";
  }
  if (
    /duplikium|trade[- ]?copier|connect to duplikium|\bslave\b|\bmaster\b|scheduleSlaveConnectSetup|is not defined|\bctrader\b|\bmt4\b|\bmt5\b/i.test(
      low,
    )
  ) {
    if (/connect|provision|credential|account|type must|only mt|not supported/i.test(low)) {
      return "We could not connect your trading account. Check your credentials and try again.";
    }
    return DEFAULT_FALLBACK;
  }
  if (
    /localhost|127\.0\.0\.1|cannot reach|non-json|npm|start:api|embedded api|web page instead of json|cloudflare/i.test(
      low,
    )
  ) {
    return "Service temporarily unavailable. Please refresh the page and try again.";
  }
  if (
    /plisio|nowpayments|ccpayment|payment is not configured|mock confirmation is disabled/i.test(
      low,
    )
  ) {
    return "Payment could not be started. Please try again or contact support.";
  }
  if (/supabase|postgres|database not configured/i.test(low)) {
    return "This feature is temporarily unavailable. Please try again later or contact support.";
  }
  if (/API unavailable|start the api/i.test(s)) {
    return "Service temporarily unavailable. Please refresh the page in a moment.";
  }
  if (/request limit|rate limit/i.test(low)) {
    return "The service is busy. Please wait about one minute and try again.";
  }
  if (/network error|unable to reach the local server|failed to fetch|load failed|networkerror/i.test(low)) {
    return "Network error. Check your connection and try again. If you just clicked Connect, wait 1–2 minutes then click Refresh.";
  }
  if (/not configured|ticketing is not configured/i.test(low)) {
    return "This feature is temporarily unavailable. Please try again later or contact support.";
  }
  return null;
}

export function clientSafeMessage(raw, fallback = DEFAULT_FALLBACK) {
  const s = String(raw ?? "").trim();
  if (!s) return fallback;

  const mapped = mapKnown(s);
  if (mapped) return mapped;

  if (VENDOR_RE.test(s) || INTERNAL_RE.test(s)) return fallback;

  if (/\(HTTP\s*\d{3}\)|\(\d{3}\)|HTTP\s+\d{3}/i.test(s)) {
    const stripped = s.replace(/\s*\([^)]*\d{3}[^)]*\)\s*/g, " ").trim();
    if (
      stripped &&
      !VENDOR_RE.test(stripped) &&
      !INTERNAL_RE.test(stripped) &&
      !mapKnown(stripped)
    ) {
      return stripped;
    }
    return fallback;
  }

  return s;
}

export { DEFAULT_FALLBACK as DEFAULT_CLIENT_MESSAGE };
